import React, { useState, useEffect, useRef } from 'react';
import { 
  X, 
  CheckCircle, 
  MessageCircle, 
  CreditCard, 
  Banknote, 
  Truck, 
  ShieldCheck, 
  AlertCircle,
  Phone,
  User,
  MapPin,
  FileText,
  Copy,
  Check,
  Upload,
  Trash2,
  Image as ImageIcon,
  Eye,
  Printer,
  QrCode,
  Star,
  Download,
  Share2
} from 'lucide-react';
import confetti from 'canvas-confetti';
import { useLanguage } from '../../context/LanguageContext';
import { useStore } from '../../context/StoreContext';
import { useCart } from '../../context/CartContext';
import { formatPrice } from '../../utils/formatters';
import { generateWhatsAppOrderUrl, generateWhatsAppOrderMessage } from '../../utils/whatsappHelper';

const CheckoutModal = () => {
  const { lang, t, isRtl } = useLanguage();
  const { settings, addOrder } = useStore();
  const { 
    cartItems, 
    subtotal, 
    deliveryFee, 
    totalAmount, 
    clearCart,
    isCheckoutOpen, 
    setIsCheckoutOpen 
  } = useCart();

  const enableCod = settings.enableCod ?? true;

  // Form State
  const [formData, setFormData] = useState({
    customerName: '',
    phone: '',
    city: '',
    address: '',
    notes: '',
    paymentMethod: enableCod ? 'cod' : 'online', // 'cod' or 'online'
    paymentAccount: 'easypaisa', // 'easypaisa' or 'bank'
    transactionRef: '',
    paymentProof: '' // Base64 compressed image of payment screenshot
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [placedOrder, setPlacedOrder] = useState(null);
  const [copiedField, setCopiedField] = useState(null);
  const [isUploadingProof, setIsUploadingProof] = useState(false);
  const [viewingQr, setViewingQr] = useState(false);
  const fileInputRef = useRef(null);

  // Sync paymentMethod if COD is disabled
  useEffect(() => {
    if (!enableCod && formData.paymentMethod === 'cod') {
      setFormData(prev => ({ ...prev, paymentMethod: 'online' }));
    }
  }, [enableCod, formData.paymentMethod]);

  // Close with Escape key & lock background scroll
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isCheckoutOpen) {
        setIsCheckoutOpen(false);
        setPlacedOrder(null);
      }
    };
    if (isCheckoutOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isCheckoutOpen, setIsCheckoutOpen]);

  if (!isCheckoutOpen) return null;

  const activePaymentMethod = enableCod ? formData.paymentMethod : 'online';
  const codAdvanceRequired = settings.codAdvanceRequired ?? true;
  const codAdvanceAmount = Number(settings.codAdvanceAmount) || 500;
  const effectiveCodAdvance = Math.min(codAdvanceAmount, totalAmount);
  const remainingCodAmount = Math.max(0, totalAmount - effectiveCodAdvance);

  const isAdvanceOrOnline = (activePaymentMethod === 'cod' && codAdvanceRequired) || activePaymentMethod === 'online';
  const hasEnteredTid = formData.transactionRef.trim().length > 0;
  const hasUploadedProof = Boolean(formData.paymentProof);
  const isPaymentProofMissing = isAdvanceOrOnline && (!hasEnteredTid || !hasUploadedProof);

  const handleProofUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert(lang === 'ur' ? 'براہ کرم صرف تصویر یا اسکرین شاٹ (JPG, PNG, WebP) منتخب کریں۔' : 'Please select an image file (JPG, PNG, WebP).');
      return;
    }

    setIsUploadingProof(true);
    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const maxW = 800;
        let w = img.width;
        let h = img.height;
        if (w > maxW) {
          h = Math.round((h * maxW) / w);
          w = maxW;
        }
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, w, h);
        const compressed = canvas.toDataURL('image/webp', 0.80);
        setFormData(prev => ({ ...prev, paymentProof: compressed }));
        setIsUploadingProof(false);
        setErrors(prev => ({ ...prev, paymentProof: null }));
      };
      img.onerror = () => {
        setIsUploadingProof(false);
        alert(lang === 'ur' ? 'اسکرین شاٹ لوڈ کرنے میں مسئلہ پیش آیا۔' : 'Failed to load screenshot image.');
      };
      img.src = event.target.result;
    };
    reader.onerror = () => {
      setIsUploadingProof(false);
      alert(lang === 'ur' ? 'فائل پڑھنے میں مسئلہ پیش آیا۔' : 'Failed to read file.');
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const validate = () => {
    const errs = {};
    if (!formData.customerName.trim()) {
      errs.customerName = lang === 'ur' ? 'براہ کرم اپنا پورا نام درج کریں۔' : 'Please enter your full name.';
    }
    if (!formData.phone.trim()) {
      errs.phone = lang === 'ur' ? 'براہ کرم موبائل یا واٹس ایپ نمبر درج کریں۔' : 'Please enter your mobile or WhatsApp number.';
    } else if (!/^[0-9+ ]{10,14}$/.test(formData.phone.replace(/\s+/g, ''))) {
      errs.phone = lang === 'ur' ? 'درست پاکستانی موبائل نمبر درج کریں (مثلاً 03001234567)۔' : 'Please enter a valid mobile number (e.g. 03001234567).';
    }
    if (!formData.city.trim()) {
      errs.city = lang === 'ur' ? 'براہ کرم اپنا شہر درج کریں۔' : 'Please enter your city.';
    }
    if (!formData.address.trim()) {
      errs.address = lang === 'ur' ? 'مکمل گلی، محلہ و پتہ درج کریں۔' : 'Please enter your complete address.';
    }

    if (isAdvanceOrOnline) {
      if (!formData.transactionRef.trim()) {
        errs.transactionRef = lang === 'ur' ? 'رقم بھیجنے کے بعد موصولہ ٹرانزیکشن ID (TID) لکھنا لازمی ہے۔' : 'Transaction Reference ID (TID) is required.';
      }
      if (!formData.paymentProof) {
        errs.paymentProof = lang === 'ur' ? 'ادائیگی کی رسید کا اسکرین شاٹ اپلوڈ کرنا لازمی ہے۔' : 'Payment receipt screenshot is required.';
      }
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handlePlaceOrder = (e) => {
    e.preventDefault();
    if (!validate() || isPaymentProofMissing) return;

    setIsSubmitting(true);

    const actualPaymentMethod = enableCod ? formData.paymentMethod : 'online';
    const isCodAdvance = actualPaymentMethod === 'cod' && codAdvanceRequired;

    // Save order into Store Context
    const orderData = {
      customerName: formData.customerName,
      phone: formData.phone,
      city: formData.city,
      address: formData.address,
      notes: formData.notes,
      paymentMethod: actualPaymentMethod,
      paymentAccount: formData.paymentAccount || 'easypaisa',
      transactionRef: formData.transactionRef.trim(),
      paymentProof: formData.paymentProof || '',
      codAdvanceRequired: isCodAdvance,
      advanceAmount: isCodAdvance ? effectiveCodAdvance : (actualPaymentMethod === 'online' ? totalAmount : 0),
      remainingAmount: isCodAdvance ? remainingCodAmount : 0,
      items: cartItems.map(item => ({
        name: item.nameUrdu,
        nameEn: item.nameEn,
        variant: item.variant?.weight,
        price: item.unitPrice,
        quantity: item.quantity,
        total: item.unitPrice * item.quantity
      })),
      subtotal,
      deliveryFee,
      totalAmount
    };

    const newOrder = addOrder(orderData);
    setPlacedOrder(newOrder);
    clearCart();
    setIsSubmitting(false);

    // Trigger WhatsApp order redirection with Screenshot support
    shareOrderWithScreenshot(newOrder);

    // Trigger celebration confetti
    try {
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 }
      });
    } catch (e) {
      console.log('Confetti effect');
    }
  };

  const shareOrderWithScreenshot = async (order) => {
    if (!order) return;

    const waParams = {
      orderId: order.id,
      customerName: order.customerName,
      phone: order.phone,
      city: order.city,
      address: order.address,
      notes: order.notes,
      cartItems: (order.items || []).map(it => ({
        nameUrdu: it.name,
        nameEn: it.nameEn,
        variant: { weight: it.variant },
        unitPrice: it.price,
        quantity: it.quantity
      })),
      subtotal: order.subtotal,
      deliveryFee: order.deliveryFee,
      totalAmount: order.totalAmount,
      paymentMethod: order.paymentMethod,
      paymentAccount: order.paymentAccount,
      transactionRef: order.transactionRef,
      hasPaymentProof: Boolean(order.paymentProof),
      storeSettings: settings,
      lang
    };

    const waUrl = generateWhatsAppOrderUrl(waParams);

    // Direct WhatsApp opening to store number
    if (typeof window !== 'undefined') {
      window.open(waUrl, '_blank');
    }
  };

  const handleSendToWhatsApp = () => {
    if (!placedOrder) return;
    shareOrderWithScreenshot(placedOrder);
  };

  const copyToClipboard = (text, fieldName) => {
    navigator.clipboard.writeText(text);
    setCopiedField(fieldName);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const renderPaymentAccounts = (purposeText) => (
    <div className="bg-white rounded-2xl p-3.5 border border-amber-200 space-y-2.5 text-xs shadow-sm text-start">
      <div className="text-[11px] font-bold text-gray-700 border-b border-gray-100 pb-1.5 flex items-center justify-between">
        <span>{purposeText}</span>
        <span className="text-[10px] text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full font-bold">
          {lang === 'ur' ? '100% محفوظ ایزی پیسہ و بینک' : '100% Secure EasyPaisa & Bank'}
        </span>
      </div>

      {/* EasyPaisa */}
      {settings.easypaisaAccount && (
        <div className="flex justify-between items-center py-0.5">
          <span className="text-gray-800">
            <strong className="text-emerald-700 font-bold">{lang === 'ur' ? 'ایزی پیسہ (EasyPaisa):' : 'EasyPaisa:'}</strong> {settings.easypaisaAccount}
          </span>
          <button
            type="button"
            onClick={() => copyToClipboard(settings.easypaisaAccount, 'easypaisa')}
            className="text-amber-900 hover:text-amber-950 text-[11px] flex items-center gap-1 font-bold bg-amber-50 hover:bg-amber-100 px-2.5 py-1 rounded-lg border border-amber-200 transition cursor-pointer"
          >
            {copiedField === 'easypaisa' ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
            <span>{copiedField === 'easypaisa' ? (lang === 'ur' ? 'کاپی شدہ' : 'Copied') : (lang === 'ur' ? 'کاپی' : 'Copy')}</span>
          </button>
        </div>
      )}

      {/* UBL Bank Details with Copy IBAN */}
      <div className="border-t border-gray-100 pt-2 space-y-1">
        <div className="flex justify-between items-start gap-2">
          <div className="flex-1 text-start">
            <div className="flex items-center gap-1.5">
              <span className="font-bold text-blue-900">{lang === 'ur' ? 'یو بی ایل بینک (UBL Bank):' : 'UBL Bank:'}</span>
              <span className="text-[10px] text-gray-500">{settings.bankAccountTitle || (lang === 'ur' ? 'سید عبدالوہاب شاہ' : 'Syed Abdul Wahab Shah')}</span>
            </div>
            <div className="font-mono text-xs font-bold text-gray-900 select-all mt-0.5 tracking-wider bg-gray-50 px-2.5 py-1 rounded border border-gray-200 inline-block">
              {settings.bankIban || 'PK56UNIL0109000290123386'}
            </div>
          </div>
          <button
            type="button"
            onClick={() => copyToClipboard(settings.bankIban || 'PK56UNIL0109000290123386', 'iban')}
            className="text-amber-900 hover:text-amber-950 text-[11px] flex items-center gap-1 font-bold bg-amber-50 hover:bg-amber-100 px-2.5 py-1 rounded-lg border border-amber-200 transition mt-1 cursor-pointer"
          >
            {copiedField === 'iban' ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
            <span>{copiedField === 'iban' ? (lang === 'ur' ? 'کاپی شدہ' : 'Copied') : (lang === 'ur' ? 'IBAN کاپی' : 'Copy IBAN')}</span>
          </button>
        </div>
      </div>

      {/* UBL Digital QR Code Box */}
      <div className="border-t border-gray-100 pt-2">
        <div className="bg-gradient-to-r from-blue-50 to-emerald-50 rounded-xl p-2.5 border border-blue-200/80 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div 
              className="w-14 h-14 bg-white rounded-lg p-1 border border-blue-300 shadow-sm shrink-0 cursor-pointer overflow-hidden group relative flex items-center justify-center"
              onClick={() => setViewingQr(true)}
              title={lang === 'ur' ? 'کیو آر کوڈ بڑا دیکھنے اور اسکین کرنے کے لیے کلک کریں' : 'Click to view enlarged QR Code'}
            >
              <img 
                src={settings.bankQrUrl || '/images/ubl-qr-code.jpg'} 
                alt="UBL Digital QR Code" 
                className="w-full h-full object-contain group-hover:scale-110 transition-transform" 
              />
              <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                <Eye className="w-3.5 h-3.5 text-white drop-shadow" />
              </div>
            </div>
            <div className="text-start">
              <span className="font-bold text-xs text-blue-950 block">
                {lang === 'ur' ? 'یو بی ایل ڈیجیٹل کیو آر کوڈ (QR Code)' : 'UBL Digital QR Code'}
              </span>
              <span className="text-[10px] text-blue-800 block">
                {lang === 'ur' ? 'کسی بھی بینکنگ ایپ یا راست (Raast) سے فوراً اسکین کریں' : 'Scan instantly from any Banking App or Raast'}
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setViewingQr(true)}
            className="px-2.5 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-bold text-[11px] flex items-center gap-1 transition shadow-sm shrink-0 cursor-pointer"
          >
            <QrCode className="w-3.5 h-3.5" />
            <span>{lang === 'ur' ? 'کیو آر دیکھیں' : 'View QR'}</span>
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div 
      className={`fixed inset-0 z-50 overflow-y-auto bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6 animate-fade-in cursor-pointer ${lang === 'ur' ? 'font-urdu' : 'font-sans'}`}
      onClick={() => {
        setIsCheckoutOpen(false);
        setPlacedOrder(null);
      }}
    >
      <div 
        className="relative bg-white w-full max-w-2xl rounded-3xl shadow-2xl overflow-hidden border border-gray-100 my-6 cursor-default max-h-[92vh] flex flex-col text-start"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header - Sticky so it is ALWAYS visible even while scrolling */}
        <div className="sticky top-0 z-30 bg-pansar-900 text-white p-4 sm:p-5 flex items-center justify-between shadow-md shrink-0">
          <div>
            <h3 className="font-bold text-lg sm:text-xl font-title">
              {placedOrder 
                ? (lang === 'ur' ? 'آرڈر کی تصدیق' : 'Order Confirmation') 
                : (lang === 'ur' ? t('checkoutTitle') : 'Checkout & Confirm Order')}
            </h3>
            <p className="text-xs text-emerald-200 mt-0.5">
              {lang === 'ur' 
                ? `${settings.storeNameUrdu || 'ہمالین پنسار سٹور'} • ${settings.dawakhanaNameUrdu || 'شیرازی مطب'}`
                : `${settings.storeNameEn || 'Himalayan Herbal Store'} • ${settings.dawakhanaNameEn || 'Shirazi Clinic'}`}
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              setIsCheckoutOpen(false);
              setPlacedOrder(null);
            }}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white text-gray-900 hover:bg-rose-600 hover:text-white transition shadow-md font-bold text-xs font-subheading border border-gray-200 cursor-pointer group"
            title={lang === 'ur' ? 'چیک آؤٹ بند کریں (Close)' : 'Close'}
          >
            <X className="w-4 h-4 group-hover:rotate-90 transition-transform duration-200" />
            <span>{lang === 'ur' ? 'بند کریں' : 'Close'}</span>
          </button>
        </div>

        {/* Modal Body: If order is placed, show Success Screen, else show Form */}
        {placedOrder ? (
          <div className="p-6 sm:p-8 text-center space-y-6">
            <div className="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto shadow-inner">
              <CheckCircle className="w-10 h-10" />
            </div>

            <div className="space-y-2">
              <h4 className="text-2xl font-black text-gray-900 font-title">
                {lang === 'ur' ? 'آپ کا آرڈر کامیابی سے موصول ہو گیا ہے!' : 'Your Order Has Been Received Successfully!'}
              </h4>
              <p className="text-sm text-gray-600">
                {lang === 'ur' ? (
                  <>محترم <strong>{placedOrder.customerName}</strong>! آپ کا آرڈر ہمارے ریکارڈ میں درج کر لیا گیا ہے۔</>
                ) : (
                  <>Dear <strong>{placedOrder.customerName}</strong>! Your order has been registered in our system.</>
                )}
              </p>
            </div>

            {/* Order Summary Receipt Box */}
            <div className="bg-gray-50 rounded-2xl p-5 text-start border border-gray-200 space-y-3">
              <div className="flex justify-between items-center border-b border-gray-200 pb-2">
                <span className="text-xs text-gray-500">{lang === 'ur' ? 'آرڈر نمبر:' : 'Order ID:'}</span>
                <span className="font-mono font-bold text-pansar-800 text-sm">#{placedOrder.id}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-500">{lang === 'ur' ? 'رابطہ نمبر:' : 'Contact Phone:'}</span>
                <span className="font-sans font-bold text-gray-800 text-xs">{placedOrder.phone}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-500">{lang === 'ur' ? 'ترسیل کا پتہ:' : 'Delivery Address:'}</span>
                <span className="text-xs text-gray-800">{placedOrder.address}, {placedOrder.city}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-500">{lang === 'ur' ? 'طریقہ ادائیگی:' : 'Payment Method:'}</span>
                <span className="text-xs font-bold text-emerald-700">
                  {placedOrder.paymentMethod === 'cod' 
                    ? (lang === 'ur' ? 'کیش آن ڈلیوری (COD)' : 'Cash on Delivery (COD)') 
                    : (lang === 'ur' ? 'آن لائن ادائیگی' : 'Online Payment')}
                </span>
              </div>
              <div className="flex justify-between items-center border-t border-gray-200 pt-2 text-sm font-extrabold text-pansar-900">
                <span>{lang === 'ur' ? 'کل رقم:' : 'Total Amount:'}</span>
                <span className="font-sans">{formatPrice(placedOrder.totalAmount, lang)}</span>
              </div>

              {/* COD Advance & Remaining Breakdown */}
              {placedOrder.paymentMethod === 'cod' && placedOrder.codAdvanceRequired && (
                <div className="bg-emerald-50 rounded-xl p-3 border border-emerald-200 space-y-1.5 text-xs">
                  <div className="flex justify-between text-amber-900 font-bold">
                    <span>{lang === 'ur' ? 'پیشگی تصدیقی ایڈوانس:' : 'Advance Verification Deposit:'}</span>
                    <span className="font-sans">{formatPrice(placedOrder.advanceAmount, lang)}</span>
                  </div>
                  <div className="flex justify-between text-emerald-900 font-black border-t border-emerald-200/80 pt-1">
                    <span>{lang === 'ur' ? 'پارسل وصولی پر واجب الادا (بقیہ رقم):' : 'Payable on Delivery (Remaining):'}</span>
                    <span className="font-sans text-emerald-800">{formatPrice(placedOrder.remainingAmount, lang)}</span>
                  </div>
                </div>
              )}

              {placedOrder.paymentAccount && (
                <div className="flex justify-between items-center text-xs">
                  <span className="text-gray-500">{lang === 'ur' ? 'ادائیگی کا اکاؤنٹ:' : 'Payment Account:'}</span>
                  <span className="font-bold text-emerald-800">
                    {placedOrder.paymentAccount === 'bank' 
                      ? (lang === 'ur' ? 'یو بی ایل بینک / ڈیجیٹل کیو آر' : 'UBL Bank / Digital QR') 
                      : (lang === 'ur' ? 'ایزی پیسہ (EasyPaisa)' : 'EasyPaisa')}
                  </span>
                </div>
              )}

              {placedOrder.transactionRef && (
                <div className="flex justify-between items-center text-xs bg-white p-2 rounded-lg border border-gray-200">
                  <span className="text-gray-500">{lang === 'ur' ? 'ٹرانزیکشن ID / TID:' : 'Transaction ID / TID:'}</span>
                  <span className="font-mono font-bold text-gray-800">{placedOrder.transactionRef}</span>
                </div>
              )}

              {placedOrder.paymentProof && (
                <div className="p-3 bg-emerald-50 rounded-xl border border-emerald-300 space-y-2 text-start">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-emerald-950 flex items-center gap-1">
                      <Check className="w-3.5 h-3.5 text-emerald-600" />
                      {lang === 'ur' ? 'ادائیگی کا اسکرین شاٹ منسلک ہے' : 'Payment receipt screenshot attached'}
                    </span>
                    <a
                      href={placedOrder.paymentProof}
                      download={`receipt-order-${placedOrder.id}.jpg`}
                      className="text-[11px] font-bold text-emerald-900 bg-white hover:bg-emerald-100 px-2.5 py-1 rounded-lg border border-emerald-300 flex items-center gap-1 transition shadow-xs cursor-pointer"
                      title={lang === 'ur' ? 'رسید اپنے موبائل میں ڈاؤن لوڈ کریں' : 'Download receipt'}
                    >
                      <Download className="w-3.5 h-3.5 text-emerald-700" />
                      <span>{lang === 'ur' ? 'رسید ڈاؤن لوڈ' : 'Download'}</span>
                    </a>
                  </div>
                  <div className="w-full max-h-40 rounded-lg overflow-hidden border border-emerald-200 bg-white flex items-center justify-center p-1.5">
                    <img src={placedOrder.paymentProof} alt="Receipt Proof" className="max-h-36 w-auto object-contain rounded" />
                  </div>
                </div>
              )}
            </div>

            {/* Post-Purchase Customer Review Guidance Card */}
            <div className="p-4 bg-gradient-to-r from-amber-50 to-emerald-50 rounded-2xl border border-amber-200 text-start space-y-2 shadow-xs">
              <div className="flex items-center gap-2 text-amber-950 font-bold text-xs">
                <Star className="w-4 h-4 text-amber-500 fill-amber-500 shrink-0" />
                <span>{lang === 'ur' ? 'محترم خریدار! دوا کے استعمال کے بعد اپنی قیمتی رائے ضرور دیجیے:' : 'Dear Customer! Please share your feedback after using:'}</span>
              </div>
              <p className="text-[11px] text-gray-700 leading-relaxed">
                {lang === 'ur' ? (
                  <>جب آپ کو پارسل موصول ہو جائے اور دوا کے استعمال سے افاقہ حاصل ہو، تو اپنے آرڈر نمبر <strong className="font-mono text-pansar-900 bg-white px-2 py-0.5 rounded border border-amber-300 font-bold">#{placedOrder.id}</strong> کے ساتھ ویب سائٹ پر متعلقہ پروڈکٹ کے نیچے ریویو درج فرمائیں تاکہ دیگر مریضوں کی بھی صحیح رہنمائی ہو سکے۔</>
                ) : (
                  <>Once you receive your parcel and experience benefits from the herbal remedy, please leave a review under the product using your order <strong className="font-mono text-pansar-900 bg-white px-2 py-0.5 rounded border border-amber-300 font-bold">#{placedOrder.id}</strong> to help guide other customers.</>
                )}
              </p>
              <div className="text-[10px] text-emerald-800 bg-white/90 p-2 rounded-xl border border-emerald-200 flex items-center gap-1.5 font-bold">
                <CheckCircle className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                <span>{lang === 'ur' ? 'آپ کی دی گئی رائے پر ویب سائٹ پر "تصدیق شدہ خریدار (Verified Purchase)" کا سبز بیج لگایا جائے گا۔' : 'Your review will be marked with a verified green "Verified Purchase" badge.'}</span>
              </div>
            </div>

            {/* WhatsApp Direct Action & Order Confirmation Actions */}
            <div className="space-y-3 pt-2">
              <div className="p-4 bg-emerald-50 rounded-2xl border border-emerald-300 text-center space-y-2.5 shadow-sm">
                <p className="font-bold text-xs sm:text-sm text-emerald-950">
                  {lang === 'ur' ? '✅ آپ کا آرڈر کامیابی سے سسٹم میں درج ہو چکا ہے!' : '✅ Your order is successfully registered in the system!'}
                </p>
                <p className="text-[11px] text-emerald-800 leading-relaxed">
                  {lang === 'ur' ? 'حکیم صاحب کو فوری واٹس ایپ میسج اور رسید بھیجنے کے لیے نیچے دیے گئے بٹن پر کلک کریں:' : 'Click below to send order confirmation and receipt screenshot directly on WhatsApp:'}
                </p>
                
                <button
                  type="button"
                  onClick={handleSendToWhatsApp}
                  className="w-full py-3.5 px-4 rounded-xl bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white font-bold text-sm flex items-center justify-center gap-2 shadow-md shadow-emerald-700/25 transition cursor-pointer"
                >
                  <MessageCircle className="w-5 h-5 fill-white" />
                  <span>{lang === 'ur' ? 'واٹس ایپ پر آرڈر اور رسید بھیجیں (WhatsApp Order)' : 'Send Order & Receipt via WhatsApp'}</span>
                </button>
                <p className="text-[10px] text-emerald-700">
                  {lang === 'ur' ? '(اگر رسید خود بخود منسلک نہ ہو تو آپ واٹس ایپ چیٹ میں بھی رسید بھیج سکتے ہیں)' : '(If receipt is not attached automatically, you can attach it directly in WhatsApp chat)'}
                </p>
              </div>

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => window.print()}
                  className="flex-1 py-3 px-4 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-800 font-bold text-xs flex items-center justify-center gap-1.5 transition cursor-pointer"
                >
                  <Printer className="w-4 h-4" />
                  <span>{lang === 'ur' ? 'رسید پرنٹ کریں (Print)' : 'Print Receipt'}</span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsCheckoutOpen(false);
                    setPlacedOrder(null);
                  }}
                  className="flex-1 py-3 px-4 rounded-xl bg-pansar-900 hover:bg-pansar-800 text-white font-bold text-xs transition shadow cursor-pointer"
                >
                  {lang === 'ur' ? 'خریداری جاری رکھیں' : 'Continue Shopping'}
                </button>
              </div>
            </div>
          </div>
        ) : (
          <form onSubmit={handlePlaceOrder} className="p-6 sm:p-8 space-y-6 flex-1 overflow-y-auto text-start">
            
            {/* Customer Inputs */}
            <div className="space-y-4">
              <h4 className="text-sm font-bold text-gray-900 border-b border-gray-100 pb-2 flex items-center gap-2">
                <User className="w-4 h-4 text-pansar-700" />
                <span>{lang === 'ur' ? 'خریدار اور ترسیل کی معلومات' : 'Customer & Shipping Information'}</span>
              </h4>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Name */}
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">
                    {t('fullName')}
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.customerName}
                    onChange={(e) => setFormData({ ...formData, customerName: e.target.value })}
                    placeholder={lang === 'ur' ? 'مثلاً: محمد احمد خان' : 'e.g. Muhammad Ahmad Khan'}
                    className={`w-full p-2.5 rounded-xl text-xs border ${
                      errors.customerName ? 'border-rose-400 bg-rose-50' : 'border-gray-200 focus:border-pansar-600'
                    } focus:outline-none`}
                  />
                  {errors.customerName && <p className="text-[11px] text-rose-500 mt-0.5">{errors.customerName}</p>}
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">
                    {t('phone')}
                  </label>
                  <input
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder={lang === 'ur' ? 'مثلاً: 03001234567' : 'e.g. 03001234567'}
                    className={`w-full p-2.5 rounded-xl text-xs border font-sans ${
                      errors.phone ? 'border-rose-400 bg-rose-50' : 'border-gray-200 focus:border-pansar-600'
                    } focus:outline-none`}
                  />
                  {errors.phone && <p className="text-[11px] text-rose-500 mt-0.5">{errors.phone}</p>}
                </div>
              </div>

              {/* City */}
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">
                  {t('city')}
                </label>
                <input
                  type="text"
                  required
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  placeholder={lang === 'ur' ? 'مثلاً: لاہور، کراچی، راولپنڈی، پشاور، ملتان...' : 'e.g. Islamabad, Lahore, Karachi, Rawalpindi...'}
                  className={`w-full p-2.5 rounded-xl text-xs border ${
                    errors.city ? 'border-rose-400 bg-rose-50' : 'border-gray-200 focus:border-pansar-600'
                  } focus:outline-none`}
                />
                {errors.city && <p className="text-[11px] text-rose-500 mt-0.5">{errors.city}</p>}
              </div>

              {/* Address */}
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">
                  {t('address')}
                </label>
                <textarea
                  rows="2"
                  required
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder={lang === 'ur' ? 'مکان نمبر، گلی، محلہ، نزد مشہور مقام...' : 'House #, Street, Area, Landmark...'}
                  className={`w-full p-2.5 rounded-xl text-xs border ${
                    errors.address ? 'border-rose-400 bg-rose-50' : 'border-gray-200 focus:border-pansar-600'
                  } focus:outline-none`}
                />
                {errors.address && <p className="text-[11px] text-rose-500 mt-0.5">{errors.address}</p>}
              </div>

              {/* Notes */}
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">
                  {t('notes')}
                </label>
                <input
                  type="text"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder={lang === 'ur' ? 'کوئی خاص ہدایت یا طبی رہنمائی کی درخواست...' : 'Any special notes or delivery instructions...'}
                  className="w-full p-2.5 rounded-xl text-xs border border-gray-200 focus:border-pansar-600 focus:outline-none"
                />
              </div>
            </div>

            {/* Payment Method Selector */}
            <div className="space-y-3 pt-2">
              <h4 className="text-sm font-bold text-gray-900 border-b border-gray-100 pb-2 flex items-center gap-2">
                <Banknote className="w-4 h-4 text-pansar-700" />
                <span>{lang === 'ur' ? 'طریقہ ادائیگی' : 'Payment Method'}</span>
              </h4>

              {enableCod ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {/* 1. COD */}
                  <label
                    onClick={() => setFormData({ ...formData, paymentMethod: 'cod' })}
                    className={`p-4 rounded-2xl border cursor-pointer transition flex items-start gap-3 ${
                      formData.paymentMethod === 'cod'
                        ? 'border-pansar-800 bg-pansar-50/70 shadow-sm'
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="paymentMethod"
                      checked={formData.paymentMethod === 'cod'}
                      onChange={() => {}}
                      className="mt-1 text-pansar-700"
                    />
                    <div>
                      <div className="font-bold text-xs text-gray-900">
                        {t('cod')}
                      </div>
                      <p className="text-[11px] text-gray-500 mt-0.5">
                        {codAdvanceRequired 
                          ? (lang === 'ur' 
                              ? `${formatPrice(effectiveCodAdvance, lang)} پیشگی ایڈوانس، بقیہ رقم پارسل وصولی پر۔`
                              : `${formatPrice(effectiveCodAdvance, lang)} advance deposit, remaining on delivery.`)
                          : (lang === 'ur'
                              ? 'پارسل موصول ہونے پر ڈلیوری بوائے کو رقم دیں۔'
                              : 'Pay cash to delivery rider upon receiving parcel.')
                        }
                      </p>
                    </div>
                  </label>

                  {/* 2. Online Bank / EasyPaisa */}
                  <label
                    onClick={() => setFormData({ ...formData, paymentMethod: 'online' })}
                    className={`p-4 rounded-2xl border cursor-pointer transition flex items-start gap-3 ${
                      formData.paymentMethod === 'online'
                        ? 'border-pansar-800 bg-pansar-50/70 shadow-sm'
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="paymentMethod"
                      checked={formData.paymentMethod === 'online'}
                      onChange={() => {}}
                      className="mt-1 text-pansar-700"
                    />
                    <div>
                      <div className="font-bold text-xs text-gray-900">
                        {t('onlinePayment')}
                      </div>
                      <p className="text-[11px] text-gray-500 mt-0.5">
                        {lang === 'ur' 
                          ? 'ایزی پیسہ یا آن لائن بینک ٹرانسفر / یو بی ایل کیو آر کوڈ (مکمل پیشگی ادائیگی)۔'
                          : 'EasyPaisa or Online Bank Transfer / UBL QR Code (Full Advance Payment).'}
                      </p>
                    </div>
                  </label>
                </div>
              ) : (
                /* When COD is OFF - Force Online payment & inform customer clearly */
                <div className="bg-amber-50/90 rounded-2xl p-4 border border-amber-300 text-xs space-y-2">
                  <div className="flex items-center gap-2 text-amber-950 font-bold">
                    <AlertCircle className="w-4 h-4 text-amber-700 shrink-0" />
                    <span>{lang === 'ur' ? 'کیش آن ڈلیوری بند ہے - مکمل بل کی پیشگی آن لائن ادائیگی' : 'Cash on Delivery Unavailable - Full Advance Payment Required'}</span>
                  </div>
                  <p className="text-[11px] text-amber-900 leading-relaxed">
                    {lang === 'ur' ? (
                      <>کیش آن ڈلیوری کی سہولت فی الحال بند ہے۔ برائے مہربانی مکمل بل کی رقم <strong className="font-bold text-pansar-950 text-xs bg-white px-2 py-0.5 rounded border border-amber-300">{formatPrice(totalAmount, lang)}</strong> درج ذیل ایزی پیسہ یا بینک اکاؤنٹ میں بھیج کر ٹرانزیکشن ID (TID) اور رسید کا اسکرین شاٹ لگائیں۔</>
                    ) : (
                      <>Cash on Delivery is currently disabled. Please transfer total order amount <strong className="font-bold text-pansar-950 text-xs bg-white px-2 py-0.5 rounded border border-amber-300">{formatPrice(totalAmount, lang)}</strong> to EasyPaisa or Bank account and attach TID and receipt screenshot.</>
                    )}
                  </p>
                </div>
              )}

              {/* COD Advance Protection Notice & Account Details */}
              {formData.paymentMethod === 'cod' && codAdvanceRequired && (
                <div className="bg-amber-50/90 rounded-2xl p-4 border border-amber-300 text-xs space-y-3 animate-fade-in shadow-sm">
                  <div className="flex items-start gap-2.5">
                    <div className="w-8 h-8 rounded-xl bg-amber-500 text-slate-950 flex items-center justify-center shrink-0">
                      <ShieldCheck className="w-5 h-5 text-amber-950" />
                    </div>
                    <div>
                      <h5 className="font-bold text-amber-950 font-subheading text-xs sm:text-sm">
                        {lang === 'ur' ? `کیش آن ڈلیوری پالیسی: ${formatPrice(effectiveCodAdvance, lang)} پیشگی وصولی` : `COD Policy: ${formatPrice(effectiveCodAdvance, lang)} Advance Deposit`}
                      </h5>
                      <p className="text-[11px] text-amber-900 mt-0.5 leading-relaxed">
                        {lang === 'ur' ? (
                          <>غیر سنجیدہ آرڈرز اور پارسل واپس ہونے پر کوریئر نقصانات کے تحفظ کے لیے، <strong>کم از کم {formatPrice(effectiveCodAdvance, lang)} پیشگی ایڈوانس</strong> وصول کیا جاتا ہے۔ جبکہ بقیہ رقم <strong>{formatPrice(remainingCodAmount, lang)}</strong> آپ کو پارسل وصول کرتے وقت کوریئر کو ادا کرنی ہوگی۔</>
                        ) : (
                          <>To safeguard against fake bookings and return courier losses, a small advance deposit of <strong>{formatPrice(effectiveCodAdvance, lang)}</strong> is required. The remaining <strong>{formatPrice(remainingCodAmount, lang)}</strong> will be paid to courier upon delivery.</>
                        )}
                      </p>
                    </div>
                  </div>

                  {/* Payment Accounts Box */}
                  {renderPaymentAccounts(lang === 'ur' ? `ایڈوانس رقم (${formatPrice(effectiveCodAdvance, lang)}) بھیجنے کے لیے اکاؤنٹس:` : `Accounts to deposit advance (${formatPrice(effectiveCodAdvance, lang)}):`)}

                  {/* Mandatory Verification Block for COD Advance */}
                  <div className="space-y-3 pt-2 border-t border-amber-200">
                    <div>
                      <label className="block text-xs font-bold text-gray-800 mb-1.5">
                        {lang === 'ur' ? '1. آپ نے کس اکاؤنٹ پر ایڈوانس رقم بھیجی ہے؟' : '1. Which account did you send payment to?'} <span className="text-rose-600">*</span>:
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, paymentAccount: 'easypaisa' }))}
                          className={`p-2.5 rounded-xl text-xs font-bold border flex items-center justify-center gap-1.5 transition cursor-pointer ${
                            (formData.paymentAccount || 'easypaisa') === 'easypaisa'
                              ? 'border-emerald-600 bg-emerald-100/80 text-emerald-950 ring-2 ring-emerald-500/20 shadow-xs'
                              : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          <span className="w-2.5 h-2.5 rounded-full bg-emerald-600"></span>
                          <span>{lang === 'ur' ? 'ایزی پیسہ (EasyPaisa)' : 'EasyPaisa'}</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, paymentAccount: 'bank' }))}
                          className={`p-2.5 rounded-xl text-xs font-bold border flex items-center justify-center gap-1.5 transition cursor-pointer ${
                            formData.paymentAccount === 'bank'
                              ? 'border-blue-600 bg-blue-100/80 text-blue-950 ring-2 ring-blue-500/20 shadow-xs'
                              : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          <span className="w-2.5 h-2.5 rounded-full bg-blue-600"></span>
                          <span>{lang === 'ur' ? 'یو بی ایل بینک / کیو آر' : 'UBL Bank / QR'}</span>
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-gray-800 mb-1">
                        {lang === 'ur' ? '2. رقم بھیجنے کے بعد موصولہ ٹرانزیکشن ریفرنس آئی ڈی (TID)' : '2. Transaction Reference ID (TID) received after payment'} <span className="text-rose-600">*</span>:
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.transactionRef}
                        onChange={(e) => {
                          setFormData({ ...formData, transactionRef: e.target.value });
                          if (errors.transactionRef) setErrors(prev => ({ ...prev, transactionRef: null }));
                        }}
                        placeholder={lang === 'ur' ? 'مثلاً: TID 94829148 (رقم بھیجنے کے بعد ملنے والا کوڈ)' : 'e.g. TID 94829148'}
                        className={`w-full p-2.5 bg-white rounded-xl border text-xs font-sans focus:outline-none ${
                          errors.transactionRef ? 'border-rose-500 bg-rose-50/40' : 'border-amber-300 focus:border-pansar-700'
                        }`}
                      />
                      {errors.transactionRef && (
                        <p className="text-[11px] text-rose-600 mt-1 font-bold">{errors.transactionRef}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-gray-800 mb-1">
                        {lang === 'ur' ? '3. ادائیگی کا اسکرین شاٹ / رسید' : '3. Payment Receipt / Screenshot'} <span className="text-rose-600">*</span>:
                      </label>

                      <input
                        type="file"
                        ref={fileInputRef}
                        accept="image/*"
                        onChange={handleProofUpload}
                        className="hidden"
                      />

                      {formData.paymentProof ? (
                        <div className="flex items-center gap-3 p-2.5 bg-emerald-50 rounded-xl border border-emerald-300">
                          <div className="w-12 h-12 rounded-lg overflow-hidden border border-emerald-200 shrink-0 bg-white">
                            <img src={formData.paymentProof} alt="Payment Receipt" className="w-full h-full object-cover" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-bold text-emerald-900 flex items-center gap-1">
                              <Check className="w-3.5 h-3.5 text-emerald-600" />
                              <span>{lang === 'ur' ? 'رسید کا اسکرین شاٹ منسلک ہو گیا ہے' : 'Receipt screenshot attached'}</span>
                            </p>
                            <span className="text-[10px] text-emerald-700">{lang === 'ur' ? 'تبدیل کرنے کے لیے بٹن دبائیں' : 'Click to change image'}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <button
                              type="button"
                              onClick={() => fileInputRef.current?.click()}
                              className="px-2.5 py-1 rounded-lg bg-white hover:bg-gray-100 text-gray-700 text-xs font-bold border border-gray-200"
                            >
                              {lang === 'ur' ? 'تبدیل' : 'Change'}
                            </button>
                            <button
                              type="button"
                              onClick={() => setFormData(prev => ({ ...prev, paymentProof: '' }))}
                              className="p-1 rounded-lg bg-rose-100 hover:bg-rose-200 text-rose-700"
                              title={lang === 'ur' ? 'حذف کریں' : 'Remove'}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          type="button"
                          disabled={isUploadingProof}
                          onClick={() => fileInputRef.current?.click()}
                          className={`w-full py-3 px-4 rounded-xl border-2 border-dashed bg-white hover:bg-amber-50/50 transition flex items-center justify-center gap-2 cursor-pointer text-xs font-bold ${
                            errors.paymentProof ? 'border-rose-400 text-rose-700 bg-rose-50/30' : 'border-amber-400 hover:border-amber-600 text-amber-950'
                          }`}
                        >
                          <Upload className="w-4 h-4 text-amber-700" />
                          <span>{isUploadingProof 
                            ? (lang === 'ur' ? 'اسکرین شاٹ لوڈ ہو رہا ہے...' : 'Uploading screenshot...') 
                            : (lang === 'ur' ? 'موبائل یا کمپیوٹر سے رسید کا اسکرین شاٹ لگائیں' : 'Upload Payment Receipt Screenshot')}
                          </span>
                        </button>
                      )}
                      {errors.paymentProof && (
                        <p className="text-[11px] text-rose-600 mt-1 font-bold">{errors.paymentProof}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Online payment instructions details */}
              {formData.paymentMethod === 'online' && (
                <div className="bg-amber-50/80 rounded-2xl p-4 border border-amber-200 text-xs space-y-3 animate-fade-in">
                  <p className="font-bold text-amber-900">
                    {lang === 'ur' ? (
                      <>برائے مہربانی مکمل بل کی رقم <strong>{formatPrice(totalAmount, lang)}</strong> درج ذیل اکاؤنٹ میں بھیجیں:</>
                    ) : (
                      <>Please transfer total order amount <strong>{formatPrice(totalAmount, lang)}</strong> to following account:</>
                    )}
                  </p>
                  {renderPaymentAccounts(lang === 'ur' ? `مکمل بل کی رقم (${formatPrice(totalAmount, lang)}) بھیجنے کے لیے اکاؤنٹس:` : `Accounts to transfer total amount (${formatPrice(totalAmount, lang)}):`)}

                  {/* Mandatory Verification Block for Online */}
                  <div className="space-y-3 pt-2 border-t border-amber-200">
                    <div>
                      <label className="block text-xs font-bold text-gray-800 mb-1.5">
                        {lang === 'ur' ? '1. آپ نے کس اکاؤنٹ پر رقم بھیجی ہے؟' : '1. Which account did you send payment to?'} <span className="text-rose-600">*</span>:
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, paymentAccount: 'easypaisa' }))}
                          className={`p-2.5 rounded-xl text-xs font-bold border flex items-center justify-center gap-1.5 transition cursor-pointer ${
                            (formData.paymentAccount || 'easypaisa') === 'easypaisa'
                              ? 'border-emerald-600 bg-emerald-100/80 text-emerald-950 ring-2 ring-emerald-500/20 shadow-xs'
                              : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          <span className="w-2.5 h-2.5 rounded-full bg-emerald-600"></span>
                          <span>{lang === 'ur' ? 'ایزی پیسہ (EasyPaisa)' : 'EasyPaisa'}</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, paymentAccount: 'bank' }))}
                          className={`p-2.5 rounded-xl text-xs font-bold border flex items-center justify-center gap-1.5 transition cursor-pointer ${
                            formData.paymentAccount === 'bank'
                              ? 'border-blue-600 bg-blue-100/80 text-blue-950 ring-2 ring-blue-500/20 shadow-xs'
                              : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          <span className="w-2.5 h-2.5 rounded-full bg-blue-600"></span>
                          <span>{lang === 'ur' ? 'یو بی ایل بینک / کیو آر' : 'UBL Bank / QR'}</span>
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-gray-800 mb-1">
                        {lang === 'ur' ? '2. رقم بھیجنے کے بعد موصولہ ٹرانزیکشن ریفرنس آئی ڈی (TID)' : '2. Transaction Reference ID (TID) received after payment'} <span className="text-rose-600">*</span>:
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.transactionRef}
                        onChange={(e) => {
                          setFormData({ ...formData, transactionRef: e.target.value });
                          if (errors.transactionRef) setErrors(prev => ({ ...prev, transactionRef: null }));
                        }}
                        placeholder={lang === 'ur' ? 'مثلاً: TID 94829148' : 'e.g. TID 94829148'}
                        className={`w-full p-2.5 bg-white rounded-xl border text-xs font-sans focus:outline-none ${
                          errors.transactionRef ? 'border-rose-500 bg-rose-50/40' : 'border-amber-300 focus:border-pansar-700'
                        }`}
                      />
                      {errors.transactionRef && (
                        <p className="text-[11px] text-rose-600 mt-1 font-bold">{errors.transactionRef}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-gray-800 mb-1">
                        {lang === 'ur' ? '3. ادائیگی کا اسکرین شاٹ / رسید' : '3. Payment Receipt / Screenshot'} <span className="text-rose-600">*</span>:
                      </label>

                      <input
                        type="file"
                        ref={fileInputRef}
                        accept="image/*"
                        onChange={handleProofUpload}
                        className="hidden"
                      />

                      {formData.paymentProof ? (
                        <div className="flex items-center gap-3 p-2.5 bg-emerald-50 rounded-xl border border-emerald-300">
                          <div className="w-12 h-12 rounded-lg overflow-hidden border border-emerald-200 shrink-0 bg-white">
                            <img src={formData.paymentProof} alt="Payment Receipt" className="w-full h-full object-cover" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-bold text-emerald-900 flex items-center gap-1">
                              <Check className="w-3.5 h-3.5 text-emerald-600" />
                              <span>{lang === 'ur' ? 'رسید کا اسکرین شاٹ منسلک ہو گیا ہے' : 'Receipt screenshot attached'}</span>
                            </p>
                            <span className="text-[10px] text-emerald-700">{lang === 'ur' ? 'تبدیل کرنے کے لیے بٹن دبائیں' : 'Click to change image'}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <button
                              type="button"
                              onClick={() => fileInputRef.current?.click()}
                              className="px-2.5 py-1 rounded-lg bg-white hover:bg-gray-100 text-gray-700 text-xs font-bold border border-gray-200"
                            >
                              {lang === 'ur' ? 'تبدیل' : 'Change'}
                            </button>
                            <button
                              type="button"
                              onClick={() => setFormData(prev => ({ ...prev, paymentProof: '' }))}
                              className="p-1 rounded-lg bg-rose-100 hover:bg-rose-200 text-rose-700"
                              title={lang === 'ur' ? 'حذف کریں' : 'Remove'}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          type="button"
                          disabled={isUploadingProof}
                          onClick={() => fileInputRef.current?.click()}
                          className={`w-full py-3 px-4 rounded-xl border-2 border-dashed bg-white hover:bg-amber-50/50 transition flex items-center justify-center gap-2 cursor-pointer text-xs font-bold ${
                            errors.paymentProof ? 'border-rose-400 text-rose-700 bg-rose-50/30' : 'border-amber-400 hover:border-amber-600 text-amber-950'
                          }`}
                        >
                          <Upload className="w-4 h-4 text-amber-700" />
                          <span>{isUploadingProof 
                            ? (lang === 'ur' ? 'اسکرین شاٹ لوڈ ہو رہا ہے...' : 'Uploading screenshot...') 
                            : (lang === 'ur' ? 'موبائل یا کمپیوٹر سے رسید کا اسکرین شاٹ لگائیں' : 'Upload Payment Receipt Screenshot')}
                          </span>
                        </button>
                      )}
                      {errors.paymentProof && (
                        <p className="text-[11px] text-rose-600 mt-1 font-bold">{errors.paymentProof}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Bill Summary */}
            <div className="bg-gray-50 rounded-2xl p-4 border border-gray-200 space-y-2 text-xs text-gray-600">
              <div className="flex justify-between">
                <span>{t('subtotal')}</span>
                <span className="font-bold font-sans text-gray-900">{formatPrice(subtotal, lang)}</span>
              </div>
              <div className="flex justify-between">
                <span>{t('deliveryFee')}</span>
                <span className="font-bold font-sans text-gray-900">
                  {deliveryFee === 0 ? <span className="text-emerald-700">{t('freeDelivery')}</span> : formatPrice(deliveryFee, lang)}
                </span>
              </div>
              <div className="flex justify-between text-base font-black text-pansar-950 border-t border-gray-200 pt-2">
                <span>{t('total')}</span>
                <span className="font-sans text-pansar-800">{formatPrice(totalAmount, lang)}</span>
              </div>

              {/* COD Advance breakdown */}
              {enableCod && formData.paymentMethod === 'cod' && codAdvanceRequired && (
                <div className="bg-emerald-50 rounded-xl p-3 border border-emerald-200 space-y-1.5 mt-2">
                  <div className="flex justify-between text-xs text-amber-900 font-bold">
                    <span>{lang === 'ur' ? 'پیشگی ایڈوانس تصدیقی فیس:' : 'Advance Verification Deposit:'}</span>
                    <span className="font-sans">{formatPrice(effectiveCodAdvance, lang)}</span>
                  </div>
                  <div className="flex justify-between text-xs text-emerald-900 font-black border-t border-emerald-200/70 pt-1">
                    <span>{lang === 'ur' ? 'بقیہ رقم (پارسل وصولی پر رائیڈر کو دیں):' : 'Remaining (Pay to Courier on Delivery):'}</span>
                    <span className="font-sans text-emerald-800">{formatPrice(remainingCodAmount, lang)}</span>
                  </div>
                </div>
              )}

              {(!enableCod || formData.paymentMethod === 'online') && (
                <div className="bg-emerald-50/70 rounded-xl p-2.5 border border-emerald-200 text-center font-bold text-emerald-800 text-xs mt-2">
                  {lang === 'ur' ? `مکمل پیشگی ادائیگی: ${formatPrice(totalAmount, lang)}` : `Full Advance Payment: ${formatPrice(totalAmount, lang)}`}
                </div>
              )}
            </div>

            {/* Submit & Cancel Buttons */}
            <div className="space-y-2.5 pt-2">
              {isPaymentProofMissing && (
                <div className="p-3 bg-amber-50 border-2 border-dashed border-amber-400 rounded-2xl text-xs text-amber-950 space-y-1">
                  <div className="font-bold flex items-center gap-1.5 text-amber-900">
                    <AlertCircle className="w-4 h-4 text-amber-600 shrink-0" />
                    <span>{lang === 'ur' ? 'آرڈر کنفرم کرنے کے لیے پیشگی رقم بھیج کر درج ذیل معلومات درج کریں:' : 'To confirm your order, transfer payment and provide details:'}</span>
                  </div>
                  <ul className="text-[11px] text-amber-900 space-y-0.5 list-disc list-inside">
                    {!hasEnteredTid && <li>{lang === 'ur' ? 'میسج میں موصولہ ٹرانزیکشن ID (TID) درج کریں۔' : 'Enter the Transaction Reference ID (TID).'}</li>}
                    {!hasUploadedProof && <li>{lang === 'ur' ? 'رقم بھیجنے کی رسید کا اسکرین شاٹ (Screenshot) اپلوڈ کریں۔' : 'Upload the payment receipt screenshot.'}</li>}
                  </ul>
                  <p className="text-[10px] text-amber-800 pt-0.5">
                    {lang === 'ur' ? '(دونوں چیزیں شامل کرنے کے بعد "آرڈر کنفرم کریں" کا بٹن آن ہو جائے گا)' : '(Button will activate once TID and screenshot are provided)'}
                  </p>
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting || isPaymentProofMissing}
                className={`w-full py-4 px-6 rounded-2xl font-bold text-sm sm:text-base shadow-lg flex items-center justify-center gap-2 font-title transition ${
                  isPaymentProofMissing
                    ? 'bg-gray-200 text-gray-400 border border-gray-300 cursor-not-allowed shadow-none'
                    : 'bg-emerald-700 hover:bg-emerald-800 text-white shadow-emerald-900/25 cursor-pointer transform hover:-translate-y-0.5'
                }`}
              >
                <CheckCircle className="w-5 h-5" />
                <span>
                  {isPaymentProofMissing 
                    ? (lang === 'ur' ? 'پہلے TID اور اسکرین شاٹ لگائیں (بٹن لاک ہے)' : 'Please enter TID & Screenshot') 
                    : (lang === 'ur' ? 'آرڈر کنفرم کریں (Confirm Order)' : 'Confirm Order')}
                </span>
              </button>

              {/* Explicit Cancel / Close Button at bottom */}
              <button
                type="button"
                onClick={() => {
                  setIsCheckoutOpen(false);
                  setPlacedOrder(null);
                }}
                className="w-full py-3 px-4 rounded-xl bg-gray-100 hover:bg-rose-50 hover:text-rose-700 text-gray-700 font-bold text-xs flex items-center justify-center gap-1.5 font-title transition cursor-pointer border border-gray-200"
              >
                <X className="w-4 h-4" />
                <span>{lang === 'ur' ? 'منسوخ کریں اور واپس جائیں (بند کریں)' : 'Cancel & Close'}</span>
              </button>
            </div>

          </form>
        )}

      </div>

      {/* Fullscreen / Enlarged QR Code Modal */}
      {viewingQr && (
        <div 
          className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4 backdrop-blur-sm cursor-pointer animate-fade-in"
          onClick={() => setViewingQr(false)}
        >
          <div 
            className="bg-white rounded-3xl p-5 max-w-sm w-full text-center space-y-3 shadow-2xl relative cursor-default text-start"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center border-b border-gray-100 pb-2.5">
              <h4 className="font-bold text-gray-900 text-sm flex items-center gap-1.5">
                <QrCode className="w-4 h-4 text-blue-600" />
                <span>{lang === 'ur' ? 'یو بی ایل کیو آر کوڈ برائے ادائیگی' : 'UBL QR Code for Payment'}</span>
              </h4>
              <button
                type="button"
                onClick={() => setViewingQr(false)}
                className="w-7 h-7 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-700 flex items-center justify-center transition cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-2 bg-gray-50 rounded-2xl border border-gray-200 flex justify-center">
              <img 
                src={settings.bankQrUrl || '/images/ubl-qr-code.jpg'} 
                alt="UBL Digital QR Code" 
                className="max-h-72 w-auto object-contain rounded-xl shadow-sm" 
              />
            </div>

            <div className="text-start space-y-1.5 text-xs bg-gray-50 p-3 rounded-xl border border-gray-200">
              <div className="flex justify-between border-b border-gray-200 pb-1">
                <span className="text-gray-500">{lang === 'ur' ? 'اکاؤنٹ ٹائٹل:' : 'Account Title:'}</span>
                <span className="font-bold text-gray-900">{settings.bankAccountTitle || (lang === 'ur' ? 'سید عبدالوہاب شاہ' : 'Syed Abdul Wahab Shah')}</span>
              </div>
              <div className="flex justify-between border-b border-gray-200 pb-1">
                <span className="text-gray-500">{lang === 'ur' ? 'بینک:' : 'Bank:'}</span>
                <span className="font-bold text-blue-800">{settings.bankName || 'United Bank Limited (UBL)'}</span>
              </div>
              <div className="flex justify-between items-center pt-0.5">
                <span className="text-gray-500">{lang === 'ur' ? 'IBAN نمبر:' : 'IBAN Number:'}</span>
                <div className="flex items-center gap-1">
                  <span className="font-mono font-bold text-gray-900 text-[11px] select-all">
                    {settings.bankIban || 'PK56UNIL0109000290123386'}
                  </span>
                  <button
                    type="button"
                    onClick={() => copyToClipboard(settings.bankIban || 'PK56UNIL0109000290123386', 'iban-modal')}
                    className="text-amber-800 hover:text-amber-950 text-[10px] font-bold bg-white px-1.5 py-0.5 rounded border border-gray-200 cursor-pointer"
                  >
                    {copiedField === 'iban-modal' ? (lang === 'ur' ? 'کاپی شدہ' : 'Copied') : (lang === 'ur' ? 'کاپی' : 'Copy')}
                  </button>
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setViewingQr(false)}
              className="w-full py-2.5 rounded-xl bg-pansar-900 hover:bg-pansar-800 text-white font-bold text-xs transition cursor-pointer"
            >
              {lang === 'ur' ? 'بند کریں' : 'Close'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CheckoutModal;

